import Dexie, { type Table } from 'dexie';
import type {
  Machine,
  ChecklistTemplate,
  InspectionRecord,
  SyncQueueEntry,
  AppSetting,
  MachineStatus,
  InspectionSyncStatus,
  QueueSyncStatus,
} from '../types';
import { seedDatabase } from './seed';

export class CNCDatabase extends Dexie {
  machines!: Table<Machine, string>;
  templates!: Table<ChecklistTemplate, string>;
  inspections!: Table<InspectionRecord, string>;
  syncQueue!: Table<SyncQueueEntry, number>;
  settings!: Table<AppSetting, string>;

  constructor() {
    super('CNC_PM_PWA_DB');
    this.version(1).stores({
      machines: 'id, name, model, serialNo, assignedTemplateId, status, cumulativeHours, updatedAt',
      templates: 'id, name, machineModel, version, updatedAt',
      inspections: 'id, machineId, templateId, frequency, inspectionDate, overallStatus, syncStatus, [machineId+inspectionDate]',
      syncQueue: '++id, queueId, recordId, endpointType, status, attempts, createdAt',
      settings: 'key, updatedAt',
    });
  }
}

export const db = new CNCDatabase();

// ==========================================
// REPOSITORY HELPER METHODS
// ==========================================

// --- Machine Repository ---
export async function getAllMachines(): Promise<Machine[]> {
  return await db.machines.toArray();
}

export async function getMachineById(id: string): Promise<Machine | undefined> {
  return await db.machines.get(id);
}

export async function saveMachine(machine: Machine): Promise<string> {
  const now = new Date().toISOString();
  const machineToSave: Machine = {
    ...machine,
    updatedAt: now,
    createdAt: machine.createdAt || now,
  };
  await db.machines.put(machineToSave);
  return machine.id;
}

export async function deleteMachine(id: string): Promise<void> {
  await db.machines.delete(id);
}

export async function updateMachineHours(id: string, hours: number): Promise<void> {
  await db.machines.update(id, {
    cumulativeHours: hours,
    updatedAt: new Date().toISOString(),
  });
}

export async function updateMachineStatus(id: string, status: MachineStatus): Promise<void> {
  await db.machines.update(id, {
    status,
    updatedAt: new Date().toISOString(),
  });
}

// --- Template Repository ---
export async function getAllTemplates(): Promise<ChecklistTemplate[]> {
  return await db.templates.toArray();
}

export async function getTemplateById(id: string): Promise<ChecklistTemplate | undefined> {
  return await db.templates.get(id);
}

export async function saveTemplate(template: ChecklistTemplate): Promise<string> {
  const now = new Date().toISOString();
  const templateToSave: ChecklistTemplate = {
    ...template,
    updatedAt: now,
    createdAt: template.createdAt || now,
  };
  await db.templates.put(templateToSave);
  return template.id;
}

export async function deleteTemplate(id: string): Promise<void> {
  await db.templates.delete(id);
}

// --- Inspection Repository ---
export async function getAllInspections(): Promise<InspectionRecord[]> {
  return await db.inspections.reverse().sortBy('inspectionDate');
}

export async function getInspectionById(id: string): Promise<InspectionRecord | undefined> {
  return await db.inspections.get(id);
}

export async function getInspectionsByMachine(machineId: string): Promise<InspectionRecord[]> {
  return await db.inspections
    .where('machineId')
    .equals(machineId)
    .reverse()
    .sortBy('inspectionDate');
}

export async function saveInspection(record: InspectionRecord): Promise<string> {
  const now = new Date().toISOString();
  const recordToSave: InspectionRecord = {
    ...record,
    updatedAt: now,
    createdAt: record.createdAt || now,
  };
  await db.inspections.put(recordToSave);

  // If inspection is pending sync, queue it
  if (recordToSave.syncStatus === 'PENDING_SYNC') {
    const gasUrl = await getSetting<string>('gas_endpoint_url');
    if (gasUrl) {
      await addToSyncQueue({
        queueId: `sync_${record.id}_${Date.now()}`,
        recordId: record.id,
        endpointType: 'google_sheets',
        targetUrl: gasUrl,
        payload: {
          action: 'single_inspection',
          inspections: [recordToSave],
          timestamp: now,
        },
        attempts: 0,
        maxAttempts: 5,
        status: 'PENDING',
        createdAt: now,
      });
    }
  }

  return record.id;
}

export async function getPendingInspections(): Promise<InspectionRecord[]> {
  return await db.inspections.where('syncStatus').equals('PENDING_SYNC').toArray();
}

export async function updateInspectionSyncStatus(
  id: string,
  syncStatus: InspectionSyncStatus,
  syncedAt?: string
): Promise<void> {
  await db.inspections.update(id, {
    syncStatus,
    syncedAt: syncedAt || (syncStatus === 'SYNCED' ? new Date().toISOString() : undefined),
    updatedAt: new Date().toISOString(),
  });
}

// --- Sync Queue Repository ---
export async function getPendingSyncQueue(): Promise<SyncQueueEntry[]> {
  return await db.syncQueue
    .where('status')
    .anyOf('PENDING', 'FAILED')
    .toArray();
}

export async function addToSyncQueue(entry: Omit<SyncQueueEntry, 'id'>): Promise<number> {
  return await db.syncQueue.add(entry as SyncQueueEntry);
}

export async function updateSyncQueueStatus(
  id: number,
  status: QueueSyncStatus,
  error?: string
): Promise<void> {
  const updateData: Partial<SyncQueueEntry> = {
    status,
    completedAt: status === 'SUCCESS' ? new Date().toISOString() : undefined,
  };
  if (error !== undefined) {
    updateData.lastError = error;
  }
  await db.syncQueue.update(id, updateData);
}

export async function clearCompletedSyncQueue(): Promise<void> {
  await db.syncQueue.where('status').equals('SUCCESS').delete();
}

// --- Settings Repository ---
export async function getSetting<T>(key: string, defaultValue?: T): Promise<T | undefined> {
  const setting = await db.settings.get(key);
  if (!setting) return defaultValue;
  return setting.value as T;
}

export async function saveSetting<T>(key: string, value: T): Promise<string> {
  await db.settings.put({
    key,
    value,
    updatedAt: new Date().toISOString(),
  });
  return key;
}

export async function deleteSetting(key: string): Promise<void> {
  await db.settings.delete(key);
}

// --- Inspector Repository ---
import type { Inspector } from '../types';

export const INSPECTORS_SETTING_KEY = 'workshop_inspectors';

export const DEFAULT_INSPECTORS: Inspector[] = [
  {
    id: 'insp-01',
    name: 'นายช่าง สมศักดิ์ มีชัย',
    role: 'หัวหน้างานซ่อมบำรุง',
    active: true,
    createdAt: '2026-01-01T08:00:00.000Z',
  },
  {
    id: 'insp-02',
    name: 'นายวิชัย รักษาการ',
    role: 'ช่างเทคนิค CNC',
    active: true,
    createdAt: '2026-01-01T08:00:00.000Z',
  },
  {
    id: 'insp-03',
    name: 'นายธีรพล ช่างกล',
    role: 'ช่างบำรุงรักษาเครื่องจักร',
    active: true,
    createdAt: '2026-01-01T08:00:00.000Z',
  },
  {
    id: 'insp-04',
    name: 'นายอนุสรณ์ มั่นคง',
    role: 'ผู้ตรวจสอบคุณภาพ QC',
    active: true,
    createdAt: '2026-01-01T08:00:00.000Z',
  },
];

export async function getAllInspectors(): Promise<Inspector[]> {
  const list = await getSetting<Inspector[]>(INSPECTORS_SETTING_KEY);
  if (!list || list.length === 0) {
    await saveSetting(INSPECTORS_SETTING_KEY, DEFAULT_INSPECTORS);
    return DEFAULT_INSPECTORS;
  }
  return list;
}

export async function getActiveInspectors(): Promise<Inspector[]> {
  const all = await getAllInspectors();
  return all.filter((i) => i.active);
}

export async function saveInspector(inspector: Inspector): Promise<Inspector[]> {
  const all = await getAllInspectors();
  const existingIdx = all.findIndex((i) => i.id === inspector.id);
  const now = new Date().toISOString();

  let updatedList: Inspector[];
  if (existingIdx >= 0) {
    updatedList = [...all];
    updatedList[existingIdx] = {
      ...inspector,
      updatedAt: now,
    };
  } else {
    updatedList = [
      ...all,
      {
        ...inspector,
        id: inspector.id || `insp-${Date.now()}`,
        createdAt: inspector.createdAt || now,
        updatedAt: now,
      },
    ];
  }

  await saveSetting(INSPECTORS_SETTING_KEY, updatedList);
  return updatedList;
}

export async function deleteInspector(id: string): Promise<Inspector[]> {
  const all = await getAllInspectors();
  const filtered = all.filter((i) => i.id !== id);
  await saveSetting(INSPECTORS_SETTING_KEY, filtered);
  return filtered;
}

// --- Default Data Seeder ---
export { seedDatabase };

export async function initDefaultData(): Promise<void> {
  await seedDatabase();
  await getAllInspectors(); // Ensures default inspectors are seeded
}
